// ─── TtsSpeakModal ───────────────────────────────────────────────────────────
// Modal para "Hablar por la cámara" — disuasión activa.
//
// Props:
//   siteId, channel — destino del comando
//   token           — JWT
//   onClose         — cerrar
//
// Muestra presets pre-aprobados por compliance + textarea custom + voz
// (Paulina / Mónica). Encola el comando vía /api/sites/{siteId}/tts_speak.
// El edge daemon lo consume con el patch del V8-5.

import { useEffect, useState } from 'react'

export default function TtsSpeakModal({ siteId, channel, channelName, token, onClose }) {
  const [presets, setPresets] = useState([])
  const [selectedPreset, setSelectedPreset] = useState(null)
  const [text, setText] = useState('')
  const [voice, setVoice] = useState('Paulina')
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    fetch(`/api/sites/${siteId}/tts_presets`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : { presets: [] })
      .then(j => { if (!cancelled) setPresets(j.presets || []) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [siteId, token])

  const choosePreset = (p) => {
    setSelectedPreset(p.id)
    setText(p.text)
  }

  const send = async () => {
    if (!text.trim()) return
    setSending(true)
    setError(null)
    setResult(null)
    try {
      const r = await fetch(`/api/sites/${siteId}/tts_speak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ channel, text, voice, preset: selectedPreset }),
      })
      const j = await r.json()
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`)
      setResult(j)
    } catch (e) {
      setError(e.message)
    } finally {
      setSending(false)
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#0a0a0a', color: '#fff',
          border: '1.5px solid rgba(255,255,255,0.3)', borderRadius: 10,
          padding: 18, minWidth: 460, maxWidth: 580,
          boxShadow: '0 8px 32px rgba(0,0,0,0.8)',
        }}
      >
        <div style={{ display: 'flex', marginBottom: 12, alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>Disuasión activa</div>
            <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>
              {channelName || `Canal ${channel}`} · vía speaker SIP del sitio
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'transparent', color: '#fff', border: 'none',
            cursor: 'pointer', fontSize: 18, padding: 0, width: 28, height: 28,
          }}>✕</button>
        </div>

        {/* Presets aprobados por compliance */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, color: '#888', marginBottom: 6, fontWeight: 600 }}>
            PRESETS APROBADOS POR COMPLIANCE
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {presets.map(p => (
              <button
                key={p.id}
                onClick={() => choosePreset(p)}
                style={{
                  textAlign: 'left', padding: '8px 10px',
                  fontSize: 11, color: '#fff',
                  background: selectedPreset === p.id ? 'rgba(37,99,235,0.5)' : 'rgba(255,255,255,0.08)',
                  border: selectedPreset === p.id ? '1px solid var(--accent)' : '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 6, cursor: 'pointer',
                }}
              >{p.text}</button>
            ))}
          </div>
        </div>

        {/* Texto custom */}
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, color: '#888', marginBottom: 4, fontWeight: 600 }}>
            TEXTO A DECIR <span style={{ color: '#666', fontWeight: 400 }}>(máx 400 chars)</span>
          </div>
          <textarea
            value={text}
            onChange={(e) => { setText(e.target.value); setSelectedPreset(null) }}
            placeholder="Escribí o seleccioná un preset arriba"
            maxLength={400}
            rows={3}
            style={{
              width: '100%', padding: 10, fontSize: 12,
              background: '#111', color: '#fff',
              border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6,
              resize: 'vertical', boxSizing: 'border-box',
            }}
          />
          <div style={{ fontSize: 10, color: '#666', textAlign: 'right', marginTop: 2 }}>
            {text.length} / 400
          </div>
        </div>

        {/* Voz */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontSize: 11, color: '#aaa' }}>Voz:</span>
          {['Paulina', 'Mónica'].map(v => (
            <label key={v} style={{ fontSize: 11, color: '#fff', cursor: 'pointer' }}>
              <input
                type="radio"
                name="voice"
                value={v}
                checked={voice === v}
                onChange={(e) => setVoice(e.target.value)}
                style={{ marginRight: 4 }}
              />{v}
            </label>
          ))}
        </div>

        {/* Resultado / error */}
        {result && (
          <div style={{
            padding: 8, marginBottom: 8, borderRadius: 6,
            background: result.backlog_warning ? 'rgba(245,158,11,0.18)' : 'rgba(16,185,129,0.18)',
            color: result.backlog_warning ? '#fbbf24' : '#34d399',
            fontSize: 11,
          }}>
            {result.message || 'Comando encolado'}
            {result.backlog_warning && (
              <div style={{ fontSize: 10, marginTop: 4, color: '#fcd34d' }}>
                ⚠ Cola con {result.position} pendientes — el edge no está consumiendo
              </div>
            )}
          </div>
        )}
        {error && (
          <div style={{
            padding: 8, marginBottom: 8, borderRadius: 6,
            background: 'rgba(239,68,68,0.18)', color: '#fca5a5', fontSize: 11,
          }}>{error}</div>
        )}

        {/* Botones */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={sending}
            style={{
              padding: '8px 14px', borderRadius: 6, fontSize: 12,
              background: 'transparent', color: '#fff',
              border: '1px solid rgba(255,255,255,0.25)',
              cursor: 'pointer',
            }}
          >Cancelar</button>
          <button
            onClick={send}
            disabled={sending || !text.trim()}
            style={{
              padding: '8px 16px', borderRadius: 6, fontSize: 12, fontWeight: 700,
              background: sending || !text.trim() ? '#444' : 'var(--accent)',
              color: '#fff', border: 'none',
              cursor: sending || !text.trim() ? 'not-allowed' : 'pointer',
            }}
          >{sending ? 'Enviando…' : '▶ Enviar al speaker'}</button>
        </div>
      </div>
    </div>
  )
}
